import React, { useState } from "react";
import {
    Box,
    HStack,
    Card,
    CardBody,
    Button,
    Text,
    Flex
} from "@chakra-ui/react";

import { useCustomTheme } from "../../contexts/CustomThemeContext";

/* Sections to render inside this page */
import ProfilePage from "../Profile/ProfilePage";
import { HikeCarsManager } from "../Travel/Hikes/HikeCarsManager";
import AdminOrdersPage from "../Orders/AdminOrdersPage";
import { DeliveryList } from "../../../src/deliveryModule/components/DeliveryList";
import ProductAdminPage from "../Products/ProductAdminPage";
import HireManagerTabsPage from "../Travel/Cars/HireManagerTabsPage";
import AdminAccommodationBookings from "../Travel/Accomodations/AdminAccommodationBookings";
import ApplicationAnalyticsDashboard from "../Analytics/ApplicationAnalyticsDashboard";
import MyProductsPage from "../Products/MyProductsPage";
import { Check, CheckCircle, CheckCircle2 } from "lucide-react";

type AccountSectionId =
    | "profile"
    | "hiking-manager"
    | "orders"
    | "deliveries"
    | "products-manager"
    | "car-hire-manager"
    | "accommodations-manager"
    | "analytics"
    | "my-products";

type HubItem = {
    id: AccountSectionId;
    name: string;
    shortName?: string;
    description: string;
};

const accountSections: HubItem[] = [
    {
        id: "profile",
        name: "Profile",
        shortName: "Profile",
        description: "Personal details and account information",
    },
    {
        id: "hiking-manager",
        name: "Hiking Manager",
        shortName: "Hikes",
        description: "Manage hikes and trip operations",
    },
    {
        id: "orders",
        name: "Orders",
        shortName: "Orders",
        description: "Track and manage orders",
    },
    {
        id: "deliveries",
        name: "Deliveries",
        shortName: "Delivery",
        description: "Manage delivery requests and tracking",
    },
    {
        id: "products-manager",
        name: "Products Manager",
        shortName: "Products",
        description: "Add and manage store products",
    },
    {
        id: "car-hire-manager",
        name: "Car Hire Manager",
        shortName: "Car Hire",
        description: "Manage car hire services",
    },
    {
        id: "accommodations-manager",
        name: "Accommodations Manager",
        shortName: "Stay",
        description: "Manage accommodation listings",
    },
    {
        id: "analytics",
        name: "Analytics",
        shortName: "Analytics",
        description: "View reports and performance insights",
    },
    {
        id: "my-products",
        name: "My Products",
        shortName: "Mine",
        description: "See products linked to your account",
    },
];

const AccountHubPage = () => {
    const { theme } = useCustomTheme();
    const [activeSection, setActiveSection] = useState<AccountSectionId>("profile");
    const cardsBorderRadii ="8px";

    const renderActiveSection = () => {
        switch (activeSection) {
            case "profile":
                return <ProfilePage />;
            case "hiking-manager":
                return <HikeCarsManager />;
            case "orders":
                return <AdminOrdersPage />;
            case "deliveries":
                return <DeliveryList />;
            case "products-manager":
                return <ProductAdminPage />;
            case "car-hire-manager":
                return <HireManagerTabsPage />;
            case "accommodations-manager":
                return <AdminAccommodationBookings />;
            case "analytics":
                return <ApplicationAnalyticsDashboard />;
            case "my-products":
                return <MyProductsPage />;
            default:
                return <ProfilePage />;
        }
    };

    return (
        <Box bg={theme.background} minH="100%" p={0}>
            <Card
                bg={theme.surface}
                border={`1px solid ${theme.border}`}
                boxShadow={theme.shadowMd}
                borderRadius={{ base: "0px", md: cardsBorderRadii }}
                overflow="hidden"
            >
                <CardBody p={0}>
                    <Box>
                        <Box
                            position="sticky"
                            top="0"
                            zIndex={5}
                            bg={theme.surface}
                            borderBottom={`1px solid ${theme.border}`}
                            px={{ base: 0, md: 4 }}
                            py={{ base: 3, md: 4 }}
                        >
                            <HStack
                                spacing={2}
                                overflowX="auto"
                                p={{ base: 3, md: 0 }}
                                sx={{
                                    "&::-webkit-scrollbar": { display: "none" },
                                    scrollbarWidth: "none",
                                }}
                            >
                                {accountSections.map((item) => {
                                    const isActive = activeSection === item.id;

                                    return (
                                        <Button
                                            key={item.id}
                                            size="sm"
                                            flexShrink={0}
                                            position="relative"
                                            borderRadius={theme.radii.sm}
                                            px={{ base: 4, md: 5 }}
                                            py={2.5}
                                            fontWeight={isActive ? "800" : "600"}
                                            color={isActive ? theme.primary : theme.text}
                                            bg={isActive ? theme.selected : theme.surfaceAlt}
                                            border={`1px solid ${isActive ? theme.primary : theme.border}`}
                                            boxShadow={isActive ? theme.shadowSm : "none"}
                                            _hover={{
                                                bg: isActive ? theme.selected : theme.hover,
                                            }}
                                            _active={{
                                                bg: isActive ? theme.selected : theme.hover,
                                            }}
                                            transition="all 0.2s ease"
                                            onClick={() => setActiveSection(item.id)}
                                        >
                                            <Flex alignItems={'centre'} gap={'8px'} justifyContent={'center'}>
                                                <Text>{item.shortName || item.name}</Text>
                                                {isActive && (
                                                    <CheckCircle2 height={'17px'} />
                                                )}
                                            </Flex>
                                        </Button>
                                    );
                                })}
                            </HStack>
                        </Box>

                        <Box minW={0}>
                            {renderActiveSection()}
                        </Box>
                    </Box>
                </CardBody>
            </Card>
        </Box>
    );
};

export default AccountHubPage;